import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalCloseButton,
    ModalBody,
    Box,
    Text,
    Flex,
    Button,
} from '@chakra-ui/react';
import { motion, AnimatePresence } from 'framer-motion';
import { useMediaQuery } from 'react-responsive';
import { useParams } from 'react-router-dom';
import {
    createSendHbarScheduledTx,
    createTokenTransferScheduledTx,
    prepareTokenAssociateTransaction,
    createContractExecuteScheduledTx,
} from '../../services/TransactionService';
import { buildCustomStrategy } from '../../signing/useStrategy';
import { clearKey } from '../../signing/KeyManager';
import { useSigningConfirmation } from '../../providers/SigningConfirmation';
import { Interface } from 'ethers';
import { AccountId } from '@hashgraph/sdk';
import { GlobalContext } from 'providers/Global';
import { ModalType, SCREEN_MD } from '../../utils/constants';
import { useToast } from 'hooks/useToast';
import { getWalletErrorMessage } from 'utils/walletErrors';
import { createSendTransaction, createContractTransaction, createTokenAssociationTransaction, createTokenTransferTransaction } from 'api/multisigAPI';
import { TxInfo } from 'types/transactions';
import { isValidAddress } from 'utils/general';
import { AbiMethodTypes } from 'types/abi';
import SendTokensForm from './CreateTransactionWizard/steps/SendTokensForm';
import ContractInteractionForm from './CreateTransactionWizard/steps/ContractInteractionForm';
import TokenAssociationForm from './CreateTransactionWizard/steps/TokenAssociationForm';
import StepExecutionSettings from './CreateTransactionWizard/steps/StepExecutionSettings';
import StepSummary from './CreateTransactionWizard/steps/StepSummary';
import {
    WizardState,
    TransactionTypeOption,
    initialWizardState,
    SendTokensData,
    ContractInteractionData,
    TokenAssociationData,
    TransactionOptions,
} from './CreateTransactionWizard/types';
import { ReactComponent as GreenWallet } from 'assets/green-wallet.svg';
import { ReactComponent as ArrowsIconGreen } from 'assets/arrows-icon-green.svg';
import { ReactComponent as PlusIconGreen } from 'assets/plus-icon-green.svg';
import { ReactComponent as ContractIconGreen } from 'assets/contract-icon-green.svg';
import { ReactComponent as ArrowDownBlack } from 'assets/arrow-down-black-icon.svg';

// --- Motion wrappers ---
const MotionBox = motion(Box);
const MotionFlex = motion(Flex);
const MotionModalContent = motion(ModalContent);

// --- Animation variants ---

// Right panel: horizontal slide with direction
const panelSlide = {
    initial: (direction: number) => ({
        opacity: 0,
        x: direction > 0 ? 80 : -80,
    }),
    animate: {
        opacity: 1,
        x: 0,
        transition: { duration: 0.35, ease: [0.4, 0, 0.2, 1] },
    },
    exit: (direction: number) => ({
        opacity: 0,
        x: direction > 0 ? -80 : 80,
        transition: { duration: 0.22, ease: [0.4, 0, 1, 1] },
    }),
};

// Left panel: gentle crossfade with subtle scale
const leftPanelFade = {
    initial: { opacity: 0, scale: 0.92 },
    animate: {
        opacity: 1,
        scale: 1,
        transition: { duration: 0.4, ease: [0.4, 0, 0.2, 1] },
    },
    exit: {
        opacity: 0,
        scale: 0.92,
        transition: { duration: 0.2, ease: [0.4, 0, 1, 1] },
    },
};

// Stagger children for the type picker buttons
const staggerContainer = {
    animate: {
        transition: { staggerChildren: 0.06, delayChildren: 0.1 },
    },
};

const staggerItem = {
    initial: { opacity: 0, y: 14 },
    animate: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.35, ease: [0.4, 0, 0.2, 1] },
    },
};

// --- Types ---

interface TransactionOption {
    icon: React.FC<React.SVGProps<SVGSVGElement>>;
    title: string;
    presetType: TransactionTypeOption;
}

const manageAssetsOptions: TransactionOption[] = [
    { icon: ArrowsIconGreen, title: 'Send tokens', presetType: 'SEND_TOKENS' },
    { icon: PlusIconGreen, title: 'Add a token', presetType: 'TOKEN_ASSOCIATE' },
];

const contractOptions: TransactionOption[] = [
    { icon: ContractIconGreen, title: 'Build a transaction', presetType: 'CONTRACT_INTERACTION' },
];

const TYPE_META: Record<TransactionTypeOption, { icon: React.FC<React.SVGProps<SVGSVGElement>>; title: string }> = {
    SEND_TOKENS: { icon: ArrowsIconGreen, title: 'Send Tokens' },
    TOKEN_ASSOCIATE: { icon: PlusIconGreen, title: 'Add a Token' },
    CONTRACT_INTERACTION: { icon: ContractIconGreen, title: 'Build a Transaction' },
};

const STEP_TITLES = [
    'Transaction Details',
    'Execution Settings',
    'Review & Submit',
];

// --- Animated progress bar ---

const AnimatedProgressBar = ({ step, totalSteps }: { step: number; totalSteps: number }) => {
    const targetPercent = (step / totalSteps) * 100;

    return (
        <Box
            w="full"
            h="6px"
            borderRadius="full"
            bg="gray.200"
            overflow="hidden"
        >
            <MotionBox
                h="full"
                borderRadius="full"
                bg="linear-gradient(90deg, #42E365 0%, #B7F133 100%)"
                initial={false}
                animate={{ width: `${targetPercent}%` }}
                transition={{
                    type: 'spring',
                    stiffness: 80,
                    damping: 18,
                    mass: 1,
                }}
            />
        </Box>
    );
};

// --- Action button ---

const ActionButton = ({ option, onClick, filled }: { option: TransactionOption; onClick: () => void; filled?: boolean }) => {
    const Icon = option.icon;
    return (
        <MotionBox
            as="button"
            w="full"
            display="flex"
            alignItems="center"
            justifyContent="center"
            gap={2}
            py={4}
            px={5}
            borderRadius={12}
            cursor="pointer"
            bg={filled ? 'linear-gradient(135deg, #B7F133 0%, #42E365 100%)' : 'transparent'}
            border={filled ? 'none' : '1.5px solid'}
            borderColor={filled ? 'transparent' : '#42E365'}
            whileHover={{
                y: -2,
                boxShadow: filled
                    ? '0 6px 20px rgba(66, 227, 101, 0.35)'
                    : '0 6px 20px rgba(66, 227, 101, 0.15)',
            }}
            whileTap={{ y: 0, scale: 0.98 }}
            variants={staggerItem}
            onClick={onClick}
        >
            <Icon style={{ width: 22, height: 22, ...(filled ? { filter: 'brightness(0)' } : {}) }} />
            <Text
                fontSize="sm"
                fontWeight="600"
                color={filled ? '#1a1a1a' : '#333'}
            >
                {option.title}
            </Text>
        </MotionBox>
    );
};

// --- Helpers ---

const convertToEvmAddress = (value: string): string => {
    if (value.startsWith('0x')) return value;
    if (/^\d+\.\d+\.\d+$/.test(value)) {
        try {
            return '0x' + AccountId.fromString(value).toSolidityAddress();
        } catch (e) {
            console.error('Failed to convert Hedera account ID to EVM address:', e);
            return value;
        }
    }
    return value;
};

// --- Main component ---

const CreateTransactionWizard = () => {
    const {
        modals: {
            [`${ModalType.CreateTransactionWizard}`]: modalState,
        },
        balance,
        currentAccount,
        connection: { accountId, hashconnectConnectorInstance },
        connectedNetwork,
        closeModal,
        triggerRefresh,
    } = useContext(GlobalContext);

    const { open, presetType } = modalState as { open: boolean; presetType?: TransactionTypeOption };

    const { walletId, multisigAccountId } = useParams();
    const { generalSuccess, showToast } = useToast();
    const { requestSignConfirmation } = useSigningConfirmation();
    const isScreenMedium = useMediaQuery({ query: `(max-width: ${SCREEN_MD})` });

    const [state, setState] = useState<WizardState>(initialWizardState);
    const [isLoading, setIsLoading] = useState(false);

    // Track navigation direction for slide animations: +1 = forward, -1 = backward
    const direction = useRef(1);

    // Derive a unique key for AnimatePresence: "picker" or "step-1", "step-2", "step-3"
    const panelKey = state.type === null ? 'picker' : `step-${state.step}`;
    const leftKey = state.type === null ? 'left-picker' : `left-${state.type}`;

    useEffect(() => {
        if (open && presetType) {
            setState(prev => ({ ...prev, type: presetType }));
        }
    }, [open, presetType]);

    const totalSteps = 3;
    const isTypePicker = state.type === null;

    const handleClose = useCallback(() => {
        setState(initialWizardState);
        closeModal(ModalType.CreateTransactionWizard);
    }, [closeModal]);

    const handleSelectType = (type: TransactionTypeOption) => {
        direction.current = 1;
        setState(prev => ({ ...prev, type }));
    };

    const handleSendTokensChange = (data: SendTokensData) => {
        setState(prev => ({ ...prev, sendTokensData: data }));
    };

    const handleContractDataChange = (data: ContractInteractionData) => {
        setState(prev => ({ ...prev, contractData: data }));
    };

    const handleTokenAssociationChange = (data: TokenAssociationData) => {
        setState(prev => ({ ...prev, tokenAssociationData: data }));
    };

    const handleOptionsChange = (options: TransactionOptions) => {
        setState(prev => ({ ...prev, options }));
    };

    const goBack = () => {
        direction.current = -1;
        if (state.step === 1) {
            setState(initialWizardState);
            return;
        }
        setState(prev => ({ ...prev, step: prev.step - 1 }));
    };

    const goNext = () => {
        direction.current = 1;
        if (state.step < totalSteps) {
            setState(prev => ({ ...prev, step: prev.step + 1 }));
        }
    };

    const isStep2Valid = useMemo(() => {
        if (state.type === 'SEND_TOKENS') {
            const { address, amount, selectedToken } = state.sendTokensData;
            if (!isValidAddress(address) || amount === '' || Number(amount) <= 0) return false;
            if (selectedToken === 'HBAR') {
                return Number(amount) <= balance;
            } else {
                const token = currentAccount.tokens?.find(t => t.token_id === selectedToken);
                if (!token) return false;
                const tokenBalance = token.balance / Math.pow(10, token.decimals);
                return Number(amount) <= tokenBalance;
            }
        }
        if (state.type === 'CONTRACT_INTERACTION') {
            const { contractId, abi, selectedMethod, methodInputs, gas } = state.contractData;
            if (!contractId || !abi || !selectedMethod || !gas) return false;
            const allInputsFilled = methodInputs.every(input => {
                if (input.type === AbiMethodTypes.Bool || input.type === 'bool') {
                    return typeof input.value === 'boolean' || input.value !== undefined;
                }
                if (input.type === AbiMethodTypes.String || input.type === 'string') {
                    return input.value !== undefined;
                }
                return input.value !== undefined && input.value !== '';
            });
            return allInputsFilled;
        }
        if (state.type === 'TOKEN_ASSOCIATE') {
            const { tokenId } = state.tokenAssociationData;
            if (!isValidAddress(tokenId)) return false;
            const isAlreadyAssociated = currentAccount.tokens?.some(t => t.token_id === tokenId);
            return !isAlreadyAssociated && state.tokenAssociationData.tokenName !== '';
        }
        return false;
    }, [state.type, state.sendTokensData, state.contractData, state.tokenAssociationData, balance, currentAccount.tokens]);

    const isStep3Valid = useMemo(() => {
        const { expirationTime } = state.options;
        if (state.type === 'TOKEN_ASSOCIATE') {
            if (!expirationTime) return false;
            const minDate = new Date();
            minDate.setMinutes(minDate.getMinutes() + 5);
            return expirationTime >= minDate;
        }
        if (expirationTime) {
            const minDate = new Date();
            minDate.setMinutes(minDate.getMinutes() + 5);
            return expirationTime >= minDate;
        }
        return true;
    }, [state.options, state.type]);

    const canProceed = useMemo(() => {
        switch (state.step) {
            case 1: return isStep2Valid;
            case 2: return isStep3Valid;
            case 3: return true;
            default: return false;
        }
    }, [state.step, isStep2Valid, isStep3Valid]);

    const isFinalStep = state.step === totalSteps;

    const encodeFunctionData = (): Uint8Array | null => {
        try {
            const { abi, selectedMethod, methodInputs } = state.contractData;
            const iface = new Interface(JSON.parse(abi));
            const values = methodInputs.map(input => {
                if (input.type === AbiMethodTypes.Bool || input.type === 'bool') return Boolean(input.value);
                if (input.type.includes('uint') || input.type.includes('int')) return input.value.toString();
                if (input.type === 'address' || input.type === AbiMethodTypes.Address) return convertToEvmAddress(input.value);
                return input.value;
            });
            const encodedData = iface.encodeFunctionData(selectedMethod, values);
            const hexString = encodedData.slice(2);
            const bytes = new Uint8Array(hexString.length / 2);
            for (let i = 0; i < hexString.length; i += 2) {
                bytes[i / 2] = parseInt(hexString.slice(i, i + 2), 16);
            }
            return bytes;
        } catch (e) {
            console.error('Error encoding function data:', e);
            return null;
        }
    };

    const handleSubmit = async () => {
        if (!hashconnectConnectorInstance || !accountId || !connectedNetwork) {
            showToast('Wallet not connected', 'error', undefined, 'top-right');
            return;
        }

        setIsLoading(true);
        try {
            const { type, sendTokensData, contractData, tokenAssociationData, options } = state;

            const expiresAt = options.expirationTime
                ? Math.floor(options.expirationTime.getTime() / 1000)
                : Math.floor(Date.now() / 1000 + 62 * 24 * 60 * 60);

            if (options.expirationTime) {
                const minDate = new Date();
                minDate.setMinutes(minDate.getMinutes() + 5);
                if (options.expirationTime.getTime() < minDate.getTime()) {
                    showToast('Expiration time must be at least 5 minutes in the future.', 'error', undefined, 'top-right');
                    setIsLoading(false);
                    return;
                }
            }

            const isCustom = connectedNetwork?.startsWith('custom-');

            // Request password confirmation for custom networks
            if (isCustom) {
                const items: { label: string; value: string }[] = [];
                let txLabel = '';
                if (type === 'SEND_TOKENS') {
                    txLabel = sendTokensData.selectedToken === 'HBAR' ? 'Send HBAR' : 'Send Token';
                    items.push({ label: 'To', value: sendTokensData.address || '' });
                    items.push({ label: 'Amount', value: `${sendTokensData.amount} ${sendTokensData.selectedToken === 'HBAR' ? 'HBAR' : sendTokensData.selectedToken}` });
                } else if (type === 'TOKEN_ASSOCIATE') {
                    txLabel = 'Token Associate';
                    items.push({ label: 'Token', value: `${tokenAssociationData.tokenName} (${tokenAssociationData.tokenId})` });
                } else if (type === 'CONTRACT_INTERACTION') {
                    txLabel = 'Contract Interaction';
                    items.push({ label: 'Contract', value: contractData.contractId });
                    if (contractData.payableAmount) items.push({ label: 'Payable', value: `${contractData.payableAmount} HBAR` });
                }
                items.push({ label: 'From', value: multisigAccountId || '' });

                const confirmed = await requestSignConfirmation({ type: txLabel, items });
                if (!confirmed) {
                    setIsLoading(false);
                    return;
                }
            }

            // Build strategy AFTER confirmation (key is freshly decrypted)
            const strategy = buildCustomStrategy(connectedNetwork, accountId);

            if (type === 'SEND_TOKENS') {
                if (sendTokensData.selectedToken === 'HBAR') {
                    const tx: TxInfo = {
                        from: multisigAccountId || '',
                        to: sendTokensData.address,
                        amount: Number(sendTokensData.amount),
                    };
                    let result;
                    if (strategy) {
                        result = await createSendHbarScheduledTx(strategy, tx, options.expirationTime || undefined, options.waitForExpiry);
                    } else {
                        result = await hashconnectConnectorInstance.createSendTokenScheduledTransaction(tx, connectedNetwork, accountId, options.expirationTime || undefined, options.waitForExpiry);
                    }
                    await createSendTransaction(tx, result, walletId!, sendTokensData.description, options.waitForExpiry, expiresAt);
                } else {
                    let result;
                    const tokenTx = {
                        from: multisigAccountId || '',
                        to: sendTokensData.address,
                        amount: Number(sendTokensData.amount),
                        tokenId: sendTokensData.selectedToken,
                        decimals: sendTokensData.tokenDecimals,
                    };
                    if (strategy) {
                        result = await createTokenTransferScheduledTx(strategy, tokenTx, options.expirationTime || undefined, options.waitForExpiry);
                    } else {
                        result = await hashconnectConnectorInstance.createTokenTransferScheduledTransaction(tokenTx, connectedNetwork, accountId, options.expirationTime || undefined, options.waitForExpiry);
                    }
                    await createTokenTransferTransaction(walletId!, {
                        toAccountId: sendTokensData.address,
                        amount: Number(sendTokensData.amount),
                        tokenId: sendTokensData.selectedToken,
                        tokenSymbol: sendTokensData.tokenSymbol,
                        tokenDecimals: sendTokensData.tokenDecimals,
                        transactionId: result.txId,
                        scheduleId: result.scheduleId,
                        description: sendTokensData.description,
                        waitForExpiry: options.waitForExpiry,
                        expiresAt,
                    });
                }
                generalSuccess();
            } else if (type === 'TOKEN_ASSOCIATE') {
                let result;
                if (strategy) {
                    result = await prepareTokenAssociateTransaction(strategy, multisigAccountId || '', tokenAssociationData.tokenId, options.expirationTime || undefined);
                } else {
                    result = await hashconnectConnectorInstance.prepareTokenAssociateTransaction(multisigAccountId || '', tokenAssociationData.tokenId, connectedNetwork, accountId, options.expirationTime || undefined);
                }
                await createTokenAssociationTransaction(walletId!, {
                    tokenId: tokenAssociationData.tokenId,
                    tokenName: tokenAssociationData.tokenName,
                    tokenSymbol: tokenAssociationData.tokenSymbol,
                    frozenTxBytes: result.frozenTxBytes || '',
                    signedTxBytes: result.signedTxBytes,
                    publicKey: result.publicKey,
                    description: tokenAssociationData.description,
                    expiresAt,
                    waitForExpiry: options.waitForExpiry,
                });
                showToast('Token association created. Waiting for additional signatures.', 'success', undefined, 'top-right');
            } else if (type === 'CONTRACT_INTERACTION') {
                const functionData = encodeFunctionData();
                if (!functionData) {
                    showToast('Failed to encode function data', 'error', undefined, 'top-right');
                    return;
                }
                let result;
                if (strategy) {
                    result = await createContractExecuteScheduledTx(strategy, contractData.contractId, functionData, parseInt(contractData.gas, 10), parseFloat(contractData.payableAmount) || 0, multisigAccountId || walletId || '', options.expirationTime || undefined, options.waitForExpiry);
                } else {
                    result = await hashconnectConnectorInstance.createContractExecuteScheduledTransaction(contractData.contractId, functionData, parseInt(contractData.gas, 10), parseFloat(contractData.payableAmount) || 0, multisigAccountId || walletId || '', connectedNetwork, accountId, options.expirationTime || undefined, options.waitForExpiry);
                }
                await createContractTransaction(walletId || '', {
                    contractId: contractData.contractId,
                    contractMethod: contractData.selectedMethod,
                    params: contractData.methodInputs.map(input => ({ type: input.type, value: input.value })),
                    scheduleId: result.scheduleId,
                    txId: result.txId,
                    gas: parseInt(contractData.gas, 10),
                    payableAmount: parseFloat(contractData.payableAmount) || 0,
                    description: contractData.description,
                    waitForExpiry: options.waitForExpiry,
                    expiresAt,
                });
                showToast('Contract interaction scheduled successfully', 'success', undefined, 'top-right');
            }

            handleClose();
            triggerRefresh();
        } catch (error: any) {
            console.error('Transaction error:', error);
            showToast(getWalletErrorMessage(error, 'Failed to create the transaction'), 'error', undefined, 'top-right');
        } finally {
            clearKey(); // Clear decrypted key from memory after signing
            setIsLoading(false);
        }
    };

    const renderStepContent = () => {
        switch (state.step) {
            case 1:
                if (state.type === 'SEND_TOKENS') return <SendTokensForm data={state.sendTokensData} onChange={handleSendTokensChange} />;
                if (state.type === 'CONTRACT_INTERACTION') return <ContractInteractionForm data={state.contractData} onChange={handleContractDataChange} />;
                if (state.type === 'TOKEN_ASSOCIATE') return <TokenAssociationForm data={state.tokenAssociationData} onChange={handleTokenAssociationChange} />;
                return null;
            case 2:
                return <StepExecutionSettings options={state.options} onChange={handleOptionsChange} transactionType={state.type ?? undefined} />;
            case 3:
                return <StepSummary state={state} />;
            default:
                return null;
        }
    };

    // --- Left panel ---
    const renderLeftPanel = () => {
        if (isTypePicker) {
            return (
                <MotionBox
                    key="left-picker"
                    variants={leftPanelFade}
                    initial="initial"
                    animate="animate"
                    exit="exit"
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    gap={5}
                >
                    <Box
                        p={5}
                        bg="white"
                        borderRadius="full"
                        boxShadow="0 8px 32px rgba(66, 227, 101, 0.12)"
                    >
                        <GreenWallet style={{ width: 80, height: 80 }} />
                    </Box>
                    <Text fontSize="2xl" fontWeight="700" textAlign="center" lineHeight="1.2">
                        New Transaction
                    </Text>
                    <Text fontSize="sm" color="gray.500" textAlign="center">
                        Choose a transaction type to get started
                    </Text>
                </MotionBox>
            );
        }

        const meta = TYPE_META[state.type!];
        const TypeIcon = meta.icon;

        return (
            <MotionBox
                key={leftKey}
                variants={leftPanelFade}
                initial="initial"
                animate="animate"
                exit="exit"
                display="flex"
                flexDirection="column"
                alignItems="center"
                gap={4}
                w="full"
            >
                <Box
                    p={5}
                    bg="white"
                    borderRadius="full"
                    boxShadow="0 8px 32px rgba(66, 227, 101, 0.12)"
                >
                    <TypeIcon style={{ width: 64, height: 64 }} />
                </Box>
                <Text fontSize="xl" fontWeight="700" textAlign="center" lineHeight="1.2">
                    {meta.title}
                </Text>
                <Text fontSize="sm" color="gray.500" textAlign="center">
                    Step {state.step} of {totalSteps}
                </Text>
                <Box w="60%" mt={-1}>
                    <AnimatedProgressBar step={state.step} totalSteps={totalSteps} />
                </Box>
            </MotionBox>
        );
    };

    // --- Right panel ---
    const renderRightPanel = () => {
        if (isTypePicker) {
            return (
                <MotionFlex
                    key="picker"
                    custom={direction.current}
                    variants={panelSlide}
                    initial="initial"
                    animate="animate"
                    exit="exit"
                    direction="column"
                    flex={1}
                    py={isScreenMedium ? 6 : 10}
                    px={isScreenMedium ? 6 : 8}
                    justify="center"
                    gap={6}
                >
                    {/* Manage assets */}
                    <MotionBox variants={staggerContainer} initial="initial" animate="animate">
                        <Text
                            fontSize="xs"
                            fontWeight="700"
                            color="gray.500"
                            textTransform="uppercase"
                            letterSpacing="wider"
                            mb={3}
                        >
                            Manage assets
                        </Text>
                        <Flex direction="column" gap={3}>
                            {manageAssetsOptions.map((option) => (
                                <ActionButton
                                    key={option.presetType}
                                    option={option}
                                    filled
                                    onClick={() => handleSelectType(option.presetType)}
                                />
                            ))}
                        </Flex>
                    </MotionBox>

                    {/* Interact with contracts */}
                    <MotionBox variants={staggerContainer} initial="initial" animate="animate">
                        <Text
                            fontSize="xs"
                            fontWeight="700"
                            color="gray.500"
                            textTransform="uppercase"
                            letterSpacing="wider"
                            mb={3}
                        >
                            Interact with contracts
                        </Text>
                        <Flex direction="column" gap={3}>
                            {contractOptions.map((option) => (
                                <ActionButton
                                    key={option.presetType}
                                    option={option}
                                    onClick={() => handleSelectType(option.presetType)}
                                />
                            ))}
                        </Flex>
                    </MotionBox>
                </MotionFlex>
            );
        }

        return (
            <MotionFlex
                key={panelKey}
                custom={direction.current}
                variants={panelSlide}
                initial="initial"
                animate="animate"
                exit="exit"
                direction="column"
                flex={1}
                py={isScreenMedium ? 4 : 6}
                px={isScreenMedium ? 4 : 6}
                overflowY="auto"
                gap={4}
            >
                {/* Back button */}
                <MotionBox
                    as="button"
                    display="inline-flex"
                    alignSelf="flex-start"
                    p={1}
                    borderRadius={6}
                    cursor="pointer"
                    whileHover={{ x: -3 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={goBack}
                >
                    <ArrowDownBlack
                        style={{ transform: 'rotate(90deg)', color: '#565656' }}
                    />
                </MotionBox>

                <Text fontSize="lg" fontWeight="700">
                    {STEP_TITLES[state.step - 1]}
                </Text>

                {renderStepContent()}

                {/* Navigation Buttons */}
                <Flex justify="center" gap={4} mt={2}>
                    {!isFinalStep ? (
                        <Button
                            w={200}
                            variant="primary"
                            borderRadius={8}
                            isDisabled={!canProceed}
                            onClick={goNext}
                        >
                            Continue
                        </Button>
                    ) : (
                        <Button
                            w={200}
                            variant="primary"
                            borderRadius={8}
                            isLoading={isLoading}
                            onClick={handleSubmit}
                        >
                            Submit Transaction
                        </Button>
                    )}
                </Flex>
            </MotionFlex>
        );
    };

    return (
        <Modal onClose={handleClose} isOpen={open} isCentered>
            <ModalOverlay />
            <MotionModalContent
                w="full"
                mx={isScreenMedium ? 4 : 0}
                borderRadius={16}
                overflow="hidden"
                maxH="90vh"
                initial={false}
                animate={{
                    maxWidth: isTypePicker
                        ? (isScreenMedium ? '586px' : '740px')
                        : (isScreenMedium ? '586px' : '860px'),
                }}
                transition={{
                    maxWidth: { type: 'spring', stiffness: 300, damping: 30 },
                }}
            >
                <ModalCloseButton
                    mt={2}
                    mr={2}
                    color="#565656"
                    zIndex={2}
                    _hover={{ border: 'none' }}
                    _focusVisible={{ outline: 'none' }}
                />
                <ModalBody p={0}>
                    <Flex direction={isScreenMedium ? 'column' : 'row'} minH={isScreenMedium ? 'auto' : '380px'}>
                        {/* Left panel */}
                        <Flex
                            direction="column"
                            justify="center"
                            align="center"
                            bg="#f8fdf4"
                            w={isScreenMedium ? 'full' : '44%'}
                            py={isScreenMedium ? 8 : 10}
                            px={8}
                            gap={5}
                            flexShrink={0}
                        >
                            <AnimatePresence mode="wait">
                                {renderLeftPanel()}
                            </AnimatePresence>
                        </Flex>

                        {/* Right panel */}
                        <Flex flex={1} overflow="hidden" position="relative">
                            <AnimatePresence mode="wait" custom={direction.current}>
                                {renderRightPanel()}
                            </AnimatePresence>
                        </Flex>
                    </Flex>
                </ModalBody>
            </MotionModalContent>
        </Modal>
    );
};

export default CreateTransactionWizard;
